import type { ReactNode } from 'react';
import { cn } from './cn.js';

/** Mirrors `ChipTone` in `@mypoz/shared`, where the status → tone map lives (MYP-22). */
export type StatusChipTone = 'muted' | 'action' | 'brand' | 'success' | 'danger';

const DOT: Record<StatusChipTone, string> = {
  muted: 'bg-muted-soft',
  action: 'bg-brand',
  brand: 'bg-brand',
  success: 'bg-success',
  danger: 'bg-danger',
};

/** `action` keeps an ink label: the dot says "your turn", the words stay plain to read. */
const LABEL: Record<StatusChipTone, string> = {
  muted: 'text-muted',
  action: 'text-ink',
  brand: 'text-brand',
  success: 'text-success',
  danger: 'text-danger',
};

export interface StatusChipProps {
  tone?: StatusChipTone;
  children: ReactNode;
}

/**
 * A square dot and an upper-case label — the one status chip for orders, packages, coupons and
 * top-ups (MYP-22). Pick the tone from `STATUS_TONES` in `@mypoz/shared`, never ad hoc, so the
 * same state reads the same everywhere. Distinct from `StatusBadge`, the filled pill used for
 * categories rather than lifecycle states.
 */
export function StatusChip({ tone = 'muted', children }: StatusChipProps) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-[2px]', DOT[tone])} />
      <span className={cn('text-xs font-semibold tracking-[0.08em] uppercase', LABEL[tone])}>
        {children}
      </span>
    </span>
  );
}
